import React, { useEffect, useState, useRef } from "react";
import {
  AddCircleOutline,
  Edit,
  Delete,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import AppraisalViewController from "../../redux/actions/AppraisalAction";
import AppraisalCycleFormModal from "./AddAppraisal";
import AppraisalViewModal from "./AppraisalView";

import "../../styles/styles.css";
import "../../styles/layout.css";
import AppraisalHistoryViewModal from "./AppraisalHistoryView";
import usePermission from "../../utils/usePermission";

const AppraisalCycleList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editCycle, setEditCycle] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEmployeeForHistory, setSelectedEmployeeForHistory] = useState(null);


  const { getAppraisalCycle, deleteAppraisalCycle, updateAppraisalCycle, getAppraisalHistory } = AppraisalViewController();
  const { appraisal, appraisal_history } = useSelector((state) => state.apprisalcycle);
  const { hasPermission } = usePermission();

  useEffect(() => {
    dispatch(getAppraisalCycle());
    dispatch(getAppraisalHistory());
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]);
  };

  const handleDelete = (id) => dispatch(deleteAppraisalCycle(id));
  const handleEdit = (cycle) => { setEditCycle(cycle); setShowModal(true); };
  const handleView = (cycle) => { setSelectedCycle(cycle); setViewModalOpen(true); };
  const handleHistoryView = (employee) => {
  setSelectedEmployeeForHistory(employee);
  setHistoryModalOpen(true);
};


  const appraisalColumnDefs = [
    { headerCheckboxSelection: true, checkboxSelection: true, maxWidth: 50, pinned: "left" },
    { headerName: "SR.No", valueGetter: (params) => params.node.rowIndex + 1, maxWidth: 80 },
    { headerName: "Employee", field: "employee_name", flex: 1 },
    { headerName: "Appraisal Period", flex: 1, valueGetter: (params) => {
      const start = params.data.cycle_start || "";
      const end = params.data.cycle_end || "";
      return start && end ? `${start} - ${end}` : "";
    }},
    { headerName: "Hike (%)", field: "hike_percentage", flex: 0.8 },
    { headerName: "Effective From", field: "effective_from", flex: 1 },
    {
  headerName: "Status",
  field: "status",
  flex: 1,
  cellRenderer: (params) => {
    const status = params.data.status;
    const id = params.data.id;

    const canChangeStatus =
      hasPermission("appraisal_cycle", "edit"); // adjust your permission check

    const statusOptions = [
      { value: "draft", label: "Draft", color: "#6c757d" },
      { value: "in_review", label: "In Review", color: "#ffc107" },
      { value: "approved", label: "Approved", color: "#28a745" },
      { value: "rejected", label: "Rejected", color: "#dc3545" },
      { value: "applied", label: "Applied to Payroll", color: "#17a2b8" },
    ];

    const handleChange = (e) => {
      const newStatus = e.target.value;
      params.setValue(newStatus);
      dispatch(updateAppraisalCycle(id, { ...params.data, status: newStatus }));
    };

    if (canChangeStatus) {
      return (
        <select
          value={status}
          onChange={handleChange}
          style={{
            backgroundColor: "white",
            color: statusOptions.find((s) => s.value === status)?.color || "black",
            border: "none",
            outline: "none",
            padding: "2px 24px 2px 4px",
            fontWeight: "bold",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            backgroundImage:
              "url('data:image/svg+xml;utf8,<svg fill=\"%23333\" height=\"10\" viewBox=\"0 0 10 10\" width=\"10\" xmlns=\"http://www.w3.org/2000/svg\"><polygon points=\"0,0 10,0 5,5\"/></svg>')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 4px center",
            backgroundSize: "10px",
            cursor: "pointer",
            width: "100%",
          }}
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value} style={{ color: opt.color }}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    } else {
      const color = statusOptions.find((s) => s.value === status)?.color || "black";
      return <span style={{ color, fontWeight: "bold" }}>{statusOptions.find((s) => s.value === status)?.label}</span>;
    }
  },
},

    { headerName: "Review Count", field: "review_count", flex: 1 },
    { headerName: "Actions", field: "actions", pinned: "right", minWidth: 120, maxWidth: 150, suppressSizeToFit: true,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <Tooltip title="View">
            <IconButton color="primary" size="small" onClick={() => handleView(params.data)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.data)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" sx={{ color: "#d32f2f" }} onClick={() => handleDelete(params.data.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ];

  const historyColumnDefs = [
    { headerCheckboxSelection: true, checkboxSelection: true, maxWidth: 50, pinned: "left" },
    { headerName: "SR.No", valueGetter: (params) => params.node.rowIndex + 1, maxWidth: 80 },
    { headerName: "Employee ID", field: "employee_id", flex: 1 },
    { headerName: "Employee Name", field: "employee_name", flex: 1 },
    { headerName: "Designation", field: "designation", flex: 1 },
    { headerName: "View History", field: "actions", pinned: "right", minWidth: 120, maxWidth: 150, suppressSizeToFit: true,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Tooltip title="View History">
            <IconButton color="primary" size="small" onClick={() => handleHistoryView(params.data)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ];

  const filteredAppraisals = (appraisal || []).filter(item =>
    `${item.employee_name || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredHistory = (appraisal_history || []).filter(item =>
    `${item.employee_name || ""} ${item.employee_id || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={() => { setEditCycle(null); setShowModal(true); }}
              sx={{ borderRadius: 2, backgroundColor: "#1976d2", textTransform: "none", paddingX: 2, height: "40px" }}
            >
              Add Appraisal
            </Button>
          )}
          <ExportButton
            gridRef={gridRef}
            fileName={activeTab === 0 ? "Appraisals.xlsx" : "AppraisalHistory.xlsx"}
            exportColumns={activeTab === 0
              ? ["employee_name","cycle_start","cycle_end","hike_percentage","effective_from","status","review_count"]
              : ["employee_id","employee_name","designation"]
            }
            disabled={selectedRows.length === 0}
            size="medium"
          />
        </div>
        <div style={{ flexGrow: 1 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ height: "40px" }}
          />
        </div>
        <PageSizeSelector pageSize={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} />
      </div>

      <div className="label-card">
        <Row className="align-items-center justify-content-between w-100">
          <Col md="auto">
            <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
              <Tab label="Appraisals" />
              <Tab label="History" />
            </Tabs>
          </Col>
        </Row>
      </div>

      <div className="ag-theme-material" style={{ height: "70vh" }}>
        {activeTab === 0 && (
          <ReusableGrid
            rowData={filteredAppraisals}
            columnDefs={appraisalColumnDefs}
            gridRef={gridRef}
            pagination
            pageSize={pageSize}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
          />
        )}
        {activeTab === 1 && (
          <ReusableGrid
            rowData={filteredHistory}
            columnDefs={historyColumnDefs}
            gridRef={gridRef}
            pagination
            pageSize={pageSize}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
          />
        )}
      </div>

      <AppraisalCycleFormModal 
      show={showModal} 
      handleClose={() => setShowModal(false)} 
      initialData={editCycle} />

      <AppraisalViewModal 
      show={viewModalOpen} 
      handleClose={() => setViewModalOpen(false)} 
      selectedCycle={selectedCycle} />

      <AppraisalHistoryViewModal
  show={historyModalOpen}
  handleClose={() => setHistoryModalOpen(false)}
  selectedEmployee={selectedEmployeeForHistory}
/>

    </div>



  );
};

export default AppraisalCycleList;
